<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { removeEmoji } from 'shared/helpers/emoji';
import { iniciaisDoNome } from 'dashboard/helper/avatar';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  src: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    required: true,
  },
  size: {
    type: Number,
    default: 32,
  },
  allowUpload: {
    type: Boolean,
    default: false,
  },
  roundedFull: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: null,
    validator: value =>
      !value || wootConstants.AVAILABILITY_STATUS_KEYS.includes(value),
  },
  inbox: {
    type: Object,
    default: null,
  },
  iconName: {
    type: String,
    default: null,
  },
  hideOfflineStatus: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['upload', 'delete']);

const { t } = useI18n();

const isImageValid = ref(true);
const fileInput = ref(null);

// Doze pares, não seis.
//
// Com 6 cores e 12 pessoas no escritório, colisão deixa de ser azar e vira
// certeza (casa dos pombos). Duas pessoas com a mesma cor derrubam justamente
// o que o avatar colorido resolve: reconhecer quem falou sem ler o nome.
//
// Os seis novos seguem a mesma construção dos originais — fundo bem claro,
// texto escuro do mesmo matiz.
//
// "Contraste suficiente" era afirmação, não medida: em 14/09/2026 medi os 24
// pares e QUATRO dos doze claros reprovavam para texto pequeno — rosa 4,17,
// verde-água 3,82, âmbar 4,27 e lima 4,37, contra o mínimo de 4,5. Os doze
// escuros passavam com folga (6,0 a 7,6). Os quatro tons de texto foram
// escurecidos o mínimo para passar (4,62 / 4,68 / 4,62 / 4,74); o fundo não
// mudou, então a cor de cada pessoa continua a mesma de relance.
// padrao-advup: paleta-de-identidade
const AVATAR_COLORS = {
  dark: [
    ['#4B143D', '#FF8DCC'], // rosa
    ['#3F220D', '#FFA366'], // laranja
    ['#2A2A2A', '#ADB1B8'], // cinza
    ['#023B37', '#0BD8B6'], // verde-água
    ['#27264D', '#A19EFF'], // índigo
    ['#1D2E62', '#9EB1FF'], // azul
    ['#0D3A1E', '#66D68C'], // verde
    ['#3D3210', '#E8C547'], // âmbar
    ['#3A1247', '#D48AFF'], // roxo
    ['#4A1218', '#FF8A94'], // rubi
    ['#0A3543', '#5CCFE6'], // ciano
    ['#2E3A0F', '#B8D94A'], // lima
  ],
  light: [
    ['#FBDCEF', '#B62682'],
    ['#FFE0BB', '#99543A'],
    ['#E8E8E8', '#60646C'],
    ['#CCF3EA', '#00765F'],
    ['#EBEBFE', '#4747C2'],
    ['#E1E9FF', '#3A5BC7'],
    ['#D6F5E0', '#1B7A44'],
    ['#FBEFC8', '#836812'],
    ['#F3E2FC', '#8B32B8'],
    ['#FFE0E3', '#B32D3A'],
    ['#D5F0F7', '#0B6B85'],
    ['#EDF5CE', '#577412'],
  ],
  default: { bg: '#E8E8E8', text: '#60646C' },
};
// padrao-advup: fim

const STATUS_CLASSES = computed(() => ({
  online: 'bg-[rgb(var(--ativo))]',
  busy: 'bg-n-amber-10',
  ...(props.hideOfflineStatus ? {} : { offline: 'bg-n-slate-10' }),
}));

const showDefaultAvatar = computed(() => !props.src && !props.name);

// A mesma regra de iniciais do produto inteiro (`helper/avatar.js`): primeiro
// e último nome, só letras. Aqui era outra conta — as duas primeiras palavras,
// primeiro caractere de cada — e a lista mostrava "F(" para
// "Fernanda (demo simulado)".
const initials = computed(() => {
  if (!props.name) return '';
  const iniciais = iniciaisDoNome(removeEmoji(props.name));
  return iniciais === '?' ? '' : iniciais;
});

// Hash sobre os CARACTERES, não sobre o comprimento.
//
// Era `props.name.length % 6`, e comprimento colide o tempo todo: "John" (4) e
// "Norma Azevedo de Sousa" (22) caem os dois no índice 4 — mesma cor. Num
// escritório com "Dr. Henrique Teixeira" e "Dra. Jullya Andrade" a lista
// inteira fica monocromática, que era a impressão de "chat sem vida".
//
// Somar os códigos distingue nomes de mesmo tamanho e mantém a cor ESTÁVEL por
// pessoa — a mesma sempre, que é o que faz o olho reconhecer sem ler.
// FNV-1a. Escolhido medindo, não por gosto: rodei quatro algoritmos contra os
// 12 nomes reais do escritório do A&T e contei cores distintas.
//
//   soma ponderada .... 7 cores, 4 grupos colidindo
//   djb2 .............. 8 cores, 2 grupos
//   sdbm .............. 7 cores, 4 grupos
//   FNV-1a ............ 9 cores, 2 grupos   <- este
//
// Duas colisões em doze é o resto do princípio da casa dos pombos com 12
// cores; zerar exigiria paleta maior, e aí as cores começam a se parecer
// entre si, que é pior.
// eslint-disable-next-line no-bitwise -- FNV-1a é definido em operações de bit;
// reescrever com aritmética daria outro algoritmo, não o mesmo mais legível.
const hashDoNome = nome => {
  let h = 2166136261;
  for (let i = 0; i < nome.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    h ^= nome.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
};

const getColorsByNameLength = computed(() => {
  if (!props.name) return AVATAR_COLORS.default;

  const index = hashDoNome(props.name) % AVATAR_COLORS.light.length;
  return {
    bg: AVATAR_COLORS.light[index][0],
    darkBg: AVATAR_COLORS.dark[index][0],
    text: AVATAR_COLORS.light[index][1],
    darkText: AVATAR_COLORS.dark[index][1],
  };
});

const containerStyles = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
}));

const borderRadiusClass = computed(() => {
  if (props.roundedFull) {
    return 'rounded-full';
  }

  // Approximates 25% of size
  if (props.size <= 16) return 'rounded'; // 4px
  if (props.size <= 24) return 'rounded-md'; // 6px
  if (props.size <= 32) return 'rounded-lg'; // 8px
  if (props.size <= 48) return 'rounded-xl'; // 12px
  return 'rounded-2xl'; // 16px
});

const avatarStyles = computed(() => ({
  ...containerStyles.value,
  backgroundColor:
    !showDefaultAvatar.value && (!props.src || !isImageValid.value)
      ? getColorsByNameLength.value.bg
      : undefined,
  color:
    !showDefaultAvatar.value && (!props.src || !isImageValid.value)
      ? getColorsByNameLength.value.text
      : undefined,
  '--dark-bg': getColorsByNameLength.value.darkBg,
  '--dark-text': getColorsByNameLength.value.darkText,
}));

const badgeStyles = computed(() => {
  const badgeSize = Math.max(props.size * 0.35, 8); // 35% of avatar size, minimum 8px
  return {
    width: `${badgeSize}px`,
    height: `${badgeSize}px`,
    top: `${props.size - badgeSize / 1.1}px`,
    left: `${props.size - badgeSize / 1.1}px`,
  };
});

const iconStyles = computed(() => ({
  fontSize: `${props.size / 1.6}px`,
}));

const initialsStyles = computed(() => ({
  fontSize: `${Math.min(props.size / 2.5, 24)}px`,
  // A INICIAL É UM NÚMERO, NÃO TEXTO CORRIDO. Sem entrelinha própria ela
  // herdava a do texto ao redor (1,5) e ficava abaixo do centro do disco —
  // medido na ficha do cartão em 14/09/2026: 16px de letra com 24 de linha.
  // `--linha-num` (0,92) é o degrau dos números, e é ele que centraliza.
  lineHeight: 'var(--linha-num)',
}));

const invalidateCurrentImage = () => {
  isImageValid.value = false;
};

const handleUploadAvatar = () => {
  fileInput.value.click();
};

const handleImageUpload = event => {
  const [file] = event.target.files;
  if (file) {
    emit('upload', {
      file,
      url: file ? URL.createObjectURL(file) : null,
    });
  }
};

const handleDeleteAvatar = () => {
  if (fileInput.value) {
    fileInput.value.value = null;
  }
  emit('delete');
};

const handleDismiss = event => {
  event.stopPropagation();
  handleDeleteAvatar();
};

watch(
  () => props.src,
  () => {
    isImageValid.value = true;
  }
);
</script>

<template>
  <span
    class="relative inline-flex group/avatar z-0 flex-shrink-0 align-middle"
    :style="containerStyles"
  >
    <!-- Status Badge -->
    <slot name="badge" :size="size">
      <div
        v-if="status && STATUS_CLASSES[status]"
        class="absolute z-20 border rounded-full border-n-slate-3"
        :style="badgeStyles"
        :class="STATUS_CLASSES[status]"
      />
      <div
        v-if="inbox && !(status && STATUS_CLASSES[status])"
        :style="badgeStyles"
        class="absolute z-20 flex items-center justify-center rounded-full bg-n-solid-1 border border-transparent flex-shrink-0"
      >
        <ChannelIcon :inbox="inbox" class="w-full h-full text-n-slate-11" />
      </div>
    </slot>

    <!-- Delete Avatar Button -->
    <div
      v-if="src && allowUpload"
      class="absolute z-20 flex items-center justify-center invisible w-6 h-6 transition-[opacity,visibility] duration-[var(--mov-2)] ease-[var(--mov-curva)] opacity-0 cursor-pointer outline outline-1 outline-n-container -top-2 ltr:-right-2 rtl:-left-2 rounded-xl bg-n-solid-3 group-hover/avatar:visible group-hover/avatar:opacity-100"
      @click="handleDismiss"
    >
      <Icon icon="i-ph-x" class="text-n-slate-11 size-4" />
    </div>

    <!-- Avatar Container -->
    <span
      role="img"
      class="relative inline-flex items-center justify-center object-cover overflow-hidden font-medium outline outline-1 -outline-offset-1 outline-[rgb(0_0_0_/_0.03)] dark:outline-[rgb(255_255_255_/_0.04)]"
      :class="[
        borderRadiusClass,
        {
          'dark:!bg-[var(--dark-bg)] dark:!text-[var(--dark-text)]':
            !showDefaultAvatar && (!src || !isImageValid),
          'bg-n-slate-3 dark:bg-n-slate-4': showDefaultAvatar,
        },
      ]"
      :style="avatarStyles"
    >
      <!-- Avatar Content -->
      <img
        v-if="src && isImageValid"
        :src="src"
        :alt="name"
        loading="lazy"
        @error="invalidateCurrentImage"
      />

      <template v-else>
        <!-- Custom Icon -->
        <Icon v-if="iconName" :icon="iconName" :style="iconStyles" />

        <!-- Initials -->
        <span
          v-else-if="!showDefaultAvatar"
          :style="initialsStyles"
          class="select-none"
        >
          {{ initials }}
        </span>

        <!-- Fallback Icon if no name or image -->
        <Icon
          v-else
          :title="t('THUMBNAIL.AUTHOR.NOT_AVAILABLE')"
          icon="i-ph-user"
          :style="iconStyles"
        />
      </template>

      <!-- Upload Overlay and Input -->
      <slot
        v-if="allowUpload || $slots.overlay"
        name="overlay"
        :size="size"
        :handle-upload="handleUploadAvatar"
        :file-input-ref="fileInput"
        :handle-image-upload="handleImageUpload"
      >
        <div
          class="absolute inset-0 z-10 flex items-center justify-center invisible w-full h-full transition-[opacity,visibility] duration-[var(--mov-2)] ease-[var(--mov-curva)] opacity-0 bg-n-alpha-black1 group-hover/avatar:visible group-hover/avatar:opacity-100"
          :class="borderRadiusClass"
          @click="handleUploadAvatar"
        >
          <Icon
            icon="i-ph-upload-simple"
            class="text-white"
            :style="{ width: `${size / 2}px`, height: `${size / 2}px` }"
          />
          <input
            v-if="allowUpload"
            ref="fileInput"
            type="file"
            accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
            class="hidden"
            @change="handleImageUpload"
          />
        </div>
      </slot>
    </span>
  </span>
</template>
